import React, { useState, useEffect, useRef, useCallback, useContext } from 'react'; // Added useContext
import { useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import apiService from '../services/api'; // Import the api service
import { useSocket } from '../context/SocketContext'; // Import useSocket
import { useClientUiSettings } from '../hooks/useClientUiSettings'; // Resolved client UI settings (Meeting 3)
// MUI Imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText'; // Add missing import
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SendIcon from '@mui/icons-material/Send';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddCommentIcon from '@mui/icons-material/AddComment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges'; // Icon for Goals
import Modal from '@mui/material/Modal'; // Or Dialog
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton'; // For the button
import Divider from '@mui/material/Divider'; // Add missing import
import CloseIcon from '@mui/icons-material/Close'; // For closing the dialog
import EventNoteIcon from '@mui/icons-material/EventNote'; // Icon for Check-in
import HistoryIcon from '@mui/icons-material/History'; // Icon for History
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // Icon for Session Review
import AssessmentIcon from '@mui/icons-material/Assessment'; // Icon for Progress Dashboard
import BookIcon from '@mui/icons-material/Book'; // Icon for Journal
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'; // Icon for Request Review
import DateRangeIcon from '@mui/icons-material/DateRange'; // Icon for Sessions
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Icon for Support
import MenuIcon from '@mui/icons-material/Menu'; // For mobile menu
import Menu from '@mui/material/Menu'; // Overflow menu in the merged chat header
import MoreVertIcon from '@mui/icons-material/MoreVert'; // Overflow trigger
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'; // Choose voice
import useMediaQuery from '@mui/material/useMediaQuery'; // For responsive design
import SpeedDial from '@mui/material/SpeedDial'; // For mobile feature menu
import SpeedDialIcon from '@mui/material/SpeedDialIcon'; // For mobile feature menu
import SpeedDialAction from '@mui/material/SpeedDialAction'; // For mobile feature menu
import Drawer from '@mui/material/Drawer'; // For mobile navigation
import ListItemIcon from '@mui/material/ListItemIcon'; // For mobile navigation items

// Import the Session components
import SessionControls from './sessions/SessionControls';
import SessionHistory from './sessions/SessionHistory';

// Import the GoalManager component
import GoalManager from './goals/GoalManager';
// Import the CheckInForm component
import CheckInForm from './checkin/CheckInForm';
// Import the CheckInHistory component
import CheckInHistory from './checkin/CheckInHistory';
// Import the JournalManager component
import JournalManager from './journal/JournalManager'; // Assuming this exists for the button
// Import the ProgressDashboard component
import ProgressDashboard from './progress/ProgressDashboard';

// Assessment Components
import AssessmentLauncher from './assessments/AssessmentLauncher';
import AssessmentRunner from './assessments/AssessmentRunner';

// Import the Speech-to-Text component
import SpeechToText from "./SpeechToText";

// Import the TextToSpeech component
import TextToSpeech from './TextToSpeech';

// Chat Components
import MessageList from './chat/MessageList';
import PersonaHeader from './chat/PersonaHeader';
import RepresentationPicker from './chat/RepresentationPicker';
import ChatInput from './chat/ChatInput';
import ChatControls from './chat/ChatControls';
import ClientVoiceSelector from './chat/ClientVoiceSelector';
import MobileInterface from './chat/MobileInterface';
import AssessmentManager from './chat/AssessmentManager';
import GoalDialogs from './chat/GoalDialogs';
import ReviewRequestModal from './chat/ReviewRequestModal';
import SupportTicketDialog from './chat/SupportTicketDialog';
import TalkMode from './chat/TalkMode';
import VoiceLivePanel from './chat/VoiceLivePanel';
import VoiceConsentDialog from './chat/VoiceConsentDialog';
import ServiceStatusBanner from './chat/ServiceStatusBanner';
import CrisisResourcesBanner from './CrisisResourcesBanner';


// Accept user and defaultVoiceId props
const ChatInterface = ({ token, user, defaultVoiceId, refreshUserData }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // Get location for state
  // Resolved client UI settings (Meeting 3) — disclaimer footer, role label, etc.
  const clientUiSettings = useClientUiSettings();
  // Persona representations picker — open conditions tracked below.
  const [representationPickerOpen, setRepresentationPickerOpen] = useState(false);
  const representationPickerAutoOpenedRef = useRef(false);
  // Check roles
  const isTherapist = user && user.role && user.role.includes('therapist');
  const isAdmin = user && user.role && user.role.includes('admin'); // Check for admin role
  
  // Add responsive detection
  const isMobile = useMediaQuery('(max-width:768px)');

  const [personas, setPersonas] = useState([]);
  const [currentPersona, setCurrentPersona] = useState('generalist');
  const [selectedPersonaForNewChat, setSelectedPersonaForNewChat] = useState('generalist');
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false); // For message sending/AI thinking
  const [processingStatus, setProcessingStatus] = useState(null); // Gen 2 processing status message
  const [initialLoading, setInitialLoading] = useState(true); // For initial setup (phase check, history/persona load)
  const [error, setError] = useState('');
  const [isNewChat, setIsNewChat] = useState(false); // Flag for starting new *main* chat
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [summaryStatus, setSummaryStatus] = useState('');
  const [chatPhase, setChatPhase] = useState('loading'); // 'loading', 'onboarding', 'main'
  const [checkInOpen, setCheckInOpen] = useState(false); // State for check-in modal
  const [assignedTherapistVoiceId, setAssignedTherapistVoiceId] = useState(null);
  const [checkInHistoryOpen, setCheckInHistoryOpen] = useState(false); // State for check-in history modal
  const [journalModalOpen, setJournalModalOpen] = useState(false); // State for Journal modal
  // Session Review State
  const [sessionReviewModalOpen, setSessionReviewModalOpen] = useState(false);
  const [sessionReviewLoading, setSessionReviewLoading] = useState(false);
  const [sessionReviewError, setSessionReviewError] = useState('');
  const [clientSummaryData, setClientSummaryData] = useState(null); // { clientSummary, reflectionPrompts }
  // Progress Dashboard State
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  // State for Review Request Feedback
  const [reviewRequestStatus, setReviewRequestStatus] = useState({ loading: false, error: '', success: '' });
  // Merged chat header (v2): voice picker + overflow menu now live in the header
  // instead of a separate ChatControls bar.
  const [voiceSelectorOpen, setVoiceSelectorOpen] = useState(false);
  const [chatMoreAnchor, setChatMoreAnchor] = useState(null);
  // Adult-9: persistent crisis-resources banner
  const [crisisResources, setCrisisResources] = useState(null); // { resources, locale, activatedReason } when active
  const [crisisBannerCollapsed, setCrisisBannerCollapsed] = useState(false);
  const [crisisBannerDismissed, setCrisisBannerDismissed] = useState(false);
  // Review Request Modal State
  const [reviewRequestModalOpen, setReviewRequestModalOpen] = useState(false);
  const [reviewRateLimitInfo, setReviewRateLimitInfo] = useState({ canRequest: true, nextAvailable: null });
  // Support Ticket State
  const [supportTicketOpen, setSupportTicketOpen] = useState(false);
  // Session Management State
  const [sessionHistoryOpen, setSessionHistoryOpen] = useState(false);
  const [clientTherapySettings, setClientTherapySettings] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionsEnabled, setSessionsEnabled] = useState(false);
  // Platform settings
  const [platformSettings, setPlatformSettings] = useState(null);
  // Invitation token for flow resolution (retrieved from localStorage)
  const [invitationToken, setInvitationToken] = useState(null);
  // Conversation mode state (ai, human, assisted) - used to control typing indicator visibility
  const [conversationMode, setConversationMode] = useState('ai');



  const messagesEndRef = useRef(null);
  const assessmentManagerRef = useRef(null);
  const goalDialogsRef = useRef(null);
  const hasInitialized = useRef(false); // Prevent double initialization in StrictMode
  const missedMessagePollRef = useRef(null); // Fallback polling timer for missed socket messages
  const socket = useSocket(); // Get socket from context
  
  // Track input method (voice vs text) for the current message
  const lastInputMethodRef = useRef('text');

  // Autoplay state
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [talkModeOpen, setTalkModeOpen] = useState(false);
  const [currentlyPlayingMessageId, setCurrentlyPlayingMessageId] = useState(null);

  // Advanced Voice Mode state
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [voiceModeEligible, setVoiceModeEligible] = useState(false);
  const [voiceConsentOpen, setVoiceConsentOpen] = useState(false);
  const [voiceConsentGiven, setVoiceConsentGiven] = useState(false);
  const [voiceConsentRequired, setVoiceConsentRequired] = useState(true);
  const voiceLivePanelRef = useRef(null);
  // Mirror voiceModeActive into a ref so socket handlers (which capture a
  // stale closure) can read the current value when guarding TTS autoplay
  // (ST-20260623-0007: AVM transcripts re-spoke via TTS on voice-mode exit).
  const voiceModeActiveRef = useRef(false);

  // Track active dialogs for autoplay pause management
  const activeDialogsRef = useRef(new Set());
  
  // Track the currently playing audio instance to prevent concurrent playback
  const playingAudioRef = useRef(null);
  
  // Debug autoplay state + log TTS playback events
  useEffect(() => {
    console.log('ChatInterface - autoplayEnabled:', autoplayEnabled, 'currentlyPlayingMessageId:', currentlyPlayingMessageId);
    if (currentlyPlayingMessageId) {
      apiService.logActivityEvent('TTS_PLAYBACK', { messageId: currentlyPlayingMessageId }, currentConversationId, token);
    }
  }, [autoplayEnabled, currentlyPlayingMessageId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Log Advanced Voice Mode start/end
  const voiceModeInitialRef = useRef(true);
  useEffect(() => {
    voiceModeActiveRef.current = voiceModeActive;
    // Skip the initial render (voiceModeActive starts as false)
    if (voiceModeInitialRef.current) {
      voiceModeInitialRef.current = false;
      return;
    }
    if (voiceModeActive) {
      apiService.logActivityEvent('VOICE_MODE_STARTED', {}, currentConversationId, token);
    } else {
      apiService.logActivityEvent('VOICE_MODE_ENDED', {}, currentConversationId, token);
    }
  }, [voiceModeActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check voice mode eligibility when conversation changes
  useEffect(() => {
    if (!currentConversationId || !token) {
      setVoiceModeEligible(false);
      return;
    }
    const checkVoiceEligibility = async () => {
      try {
        const [eligibility, config] = await Promise.all([
          apiService.checkVoiceModeEligibility(token, currentConversationId),
          apiService.getVoiceModeConfig(token).catch(() => null)
        ]);
        setVoiceModeEligible(eligibility?.eligible || false);
        if (config?.safety?.consentRequired === false) {
          setVoiceConsentRequired(false);
          setVoiceConsentGiven(true); // Skip consent if admin disabled it
        }
      } catch (err) {
        setVoiceModeEligible(false);
      }
    };
    checkVoiceEligibility();
    // Re-check when chatPhase changes: voice mode is server-gated off during
    // onboarding, so we must re-query once onboarding completes (phase flips to
    // 'main') for AVM to become available without a page reload.
  }, [currentConversationId, token, chatPhase]);

  // Track last message to detect new messages for autoplay
  const lastMessageRef = useRef(null);
  const lastMessageWhenAutoplayEnabledRef = useRef(null);
  
  // Track when autoplay is enabled
  useEffect(() => {
    if (autoplayEnabled) {
      // Remember the last message when autoplay is turned on
      if (messages.length > 0) {
        lastMessageWhenAutoplayEnabledRef.current = messages[messages.length - 1];
        lastMessageRef.current = messages[messages.length - 1];
      }
      console.log('ChatInterface: Autoplay enabled, tracking from current message');
    }
  }, [autoplayEnabled]);
  
  // Effect to trigger autoplay for new AI/therapist messages
  // Auto-open the persona representations picker on first chat when:
  //   - representations are enabled platform-wide
  //   - the client hasn't picked one yet
  //   - there's no platform default set (so the picker is the only path)
  //   - the chat is in 'main' phase (not loading / not onboarding)
  //   - the conversation isn't in an active crisis state (defensive — see below)
  // Only fires once per session via the ref guard.
  //
  // The crisis-state guard is defensive: the ref guard above already prevents
  // the picker from re-opening within a session. But if the ref gets reset
  // (page reload, session resume, dev hot-reload), an active crisis (banner
  // showing, handoff in progress) should still suppress the picker — choosing
  // an avatar is not the right interaction at that moment.
  useEffect(() => {
    if (representationPickerAutoOpenedRef.current) return;
    if (chatPhase !== 'main') return;
    if (!clientUiSettings?.representationsEnabled) return;
    if (clientUiSettings?.selectedRepresentation) return; // already chosen
    if (clientUiSettings?.defaultRepresentationKey) return; // platform default in play
    const allowed = clientUiSettings?.allowedRepresentationKeys || [];
    if (allowed.length === 0) return; // nothing to pick
    if (crisisResources) return; // crisis-banner active — defer the picker
    representationPickerAutoOpenedRef.current = true;
    setRepresentationPickerOpen(true);
  }, [
    chatPhase,
    clientUiSettings?.representationsEnabled,
    clientUiSettings?.selectedRepresentation,
    clientUiSettings?.defaultRepresentationKey,
    clientUiSettings?.allowedRepresentationKeys,
    crisisResources
  ]);

  useEffect(() => {
    if (!autoplayEnabled || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    
    // Check if this is a new message (not the same as our last tracked message)
    if (lastMessage && lastMessage !== lastMessageRef.current) {
      lastMessageRef.current = lastMessage;

      // Don't autoplay while Advanced Voice Mode is active — its transcripts are
      // already spoken live; re-speaking them via TTS (and especially on voice-
      // mode exit) is always wrong (ST-20260623-0007). The ref is still advanced
      // above so the message is marked "seen" and won't autoplay after exit.
      if (voiceModeActive) return;

      // Check if it's an AI or therapist message that should autoplay
      if (lastMessage.senderRole === 'ai' || lastMessage.senderRole === 'human_therapist') {
        const messageId = lastMessage._id || lastMessage.timestamp?.toString() || Date.now().toString();
        console.log('ChatInterface: New AI/therapist message detected, triggering autoplay:', messageId, lastMessage);
        setCurrentlyPlayingMessageId(messageId);
      }
    }
  }, [messages, autoplayEnabled, voiceModeActive]);

// State for therapist voice settings
const [therapistVoiceSettings, setTherapistVoiceSettings] = useState(null);

// Fetch therapist's voice settings (includes client-specific overrides AND
// the client's own catalog pick resolved through the unified voice resolver).
// Wrapped in useCallback so we can re-run on demand — e.g. after the client
// picks a new voice, ChatControls invokes this to refresh assignedTherapistVoiceId.
const fetchTherapistVoiceSettings = useCallback(async () => {
  if (!token || !user?.assignedTherapist) {
    setTherapistVoiceSettings(null);
    setAssignedTherapistVoiceId(null);
    return;
  }

  try {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const response = await fetch(`${API_BASE_URL}/client/voice-settings`, {
      headers: { 'x-auth-token': token }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('[ChatInterface] Fetched voice settings:', data);
      setTherapistVoiceSettings(data.therapistSettings);
      // Prefer the resolver-resolved voice (which honours the client's catalog
      // pick at hierarchy level 1). Fall back to legacy precedence if absent.
      const resolvedId = data.resolvedAiTherapistVoice?.voiceId;
      setAssignedTherapistVoiceId(
        resolvedId
          || data.voiceSettings?.aiTherapistVoice
          || data.therapistSettings?.elevenLabsVoiceId
      );
    } else {
      console.error('Failed to fetch voice settings:', response.status);
      // Fall back to legacy API
      const data = await apiService.getAssignedTherapistVoice(token);
      setAssignedTherapistVoiceId(data.voiceId);
    }
  } catch (error) {
    console.error('Failed to fetch voice settings:', error);
    setAssignedTherapistVoiceId(null);
    setTherapistVoiceSettings(null);
  }
}, [token, user?.assignedTherapist]);

useEffect(() => {
  fetchTherapistVoiceSettings();
}, [fetchTherapistVoiceSettings]);

// When the client picks a new persona representation (or any other
// clientUi setting changes), the resolved AI-therapist voice on the server
// can change too. Re-fetch so subsequent AI messages play the new voice
// without requiring a full page reload. The same custom event is already
// fired by RepresentationPicker and the admin/therapist editors.
useEffect(() => {
  const onClientUiChanged = () => fetchTherapistVoiceSettings();
  window.addEventListener('aimhi:clientUiSettingsChanged', onClientUiChanged);
  return () => window.removeEventListener('aimhi:clientUiSettingsChanged', onClientUiChanged);
}, [fetchTherapistVoiceSettings]);

// Fetch platform settings
useEffect(() => {
  const fetchSettings = async () => {
    if (!token) return;
    try {
      const settings = await apiService.getPlatformSettings(token);
      setPlatformSettings(settings);
    } catch (error) {
      console.error('Failed to load platform settings:', error);
    }
  };
  
  fetchSettings();
}, [token]);

// Voice selection helper function. Both AI and human-therapist messages now
// use assignedTherapistVoiceId (server-resolved by getClientOwnVoiceSettings,
// which honours the client's catalog pick at hierarchy level 1). The legacy
// defaultVoiceId is kept only as a final fallback.
const getVoiceIdForMessage = useCallback((msg) => {
  if (msg.senderRole === 'human_therapist') {
    return assignedTherapistVoiceId || defaultVoiceId;
  }
  if (msg.senderRole === 'ai' || msg.senderRole === 'ai_therapist') {
    return assignedTherapistVoiceId || defaultVoiceId;
  }
  return null;
}, [assignedTherapistVoiceId, defaultVoiceId]);

// Autoplay pause/resume management
const pauseAutoplay = useCallback((dialogId) => {
  console.log('Pausing autoplay for dialog:', dialogId);
  activeDialogsRef.current.add(dialogId);
  setAutoplayPaused(true);
  // Also pause voice mode if active
  voiceLivePanelRef.current?.pause();
}, []);

const resumeAutoplay = useCallback((dialogId) => {
  console.log('Resuming autoplay for dialog:', dialogId);
  activeDialogsRef.current.delete(dialogId);
  if (activeDialogsRef.current.size === 0) {
    setAutoplayPaused(false);
    // Resume voice mode if it was paused (and still active)
    if (voiceModeActive) {
      voiceLivePanelRef.current?.resume();
    }
  }
}, [voiceModeActive]);

const stopCurrentAudio = useCallback(() => {
  console.log('Stopping current audio playback');
  // Stop the actual audio if playing
  if (playingAudioRef.current) {
    console.log('Stopping WebSocket audio instance');
    if (playingAudioRef.current.stop) {
      playingAudioRef.current.stop();
    }
    playingAudioRef.current = null;
  }
  setCurrentlyPlayingMessageId(null);
}, []);

// Register/unregister playing audio instances to prevent concurrent playback
const setPlayingAudio = useCallback((audioInstance) => {
  console.log('Setting playing audio instance:', audioInstance ? 'new instance' : 'null');
  // Stop previous audio if any
  if (playingAudioRef.current && playingAudioRef.current !== audioInstance) {
    console.log('Stopping previous audio instance');
    if (playingAudioRef.current.stop) {
      playingAudioRef.current.stop();
    }
  }
  playingAudioRef.current = audioInstance;
}, []);

// While the mic is open, TTS must not play: the speech recognizer picks up the
// AI's spoken reply through the speaker and transcribes it into the client's
// message. Stop any playing audio and hold autoplay until listening ends.
const handleSttListeningStart = useCallback(() => {
  stopCurrentAudio();
  pauseAutoplay('stt-listening');
  apiService.logActivityEvent('STT_SESSION_STARTED', {}, currentConversationId, token);
}, [stopCurrentAudio, pauseAutoplay, currentConversationId, token]);

const handleSttListeningEnd = useCallback(() => {
  resumeAutoplay('stt-listening');
  apiService.logActivityEvent('STT_SESSION_ENDED', {}, currentConversationId, token);
}, [resumeAutoplay, currentConversationId, token]);


  // --- Load Client Therapy Settings ---
  useEffect(() => {
    const loadTherapySettings = async () => {
      if (!token || !user) return;
      try {
        let settings = { sessionMode: 'continuous', sessionLength: 45, sessionFrequency: 'weekly', enforceTimeBoundaries: false };
        try { settings = await apiService.getClientTherapySettings(user._id, token); } catch (err) { console.log('No custom therapy settings found, using defaults.'); }
        setClientTherapySettings(settings);
        const sessionsActive = settings.sessionMode === 'structured' || settings.sessionMode === 'hybrid';
        setSessionsEnabled(sessionsActive); 
      } catch (err) { console.error('Error in loadTherapySettings:', err); setSessionsEnabled(false); }
    };
    if (token && user && user.isOnboarded) { loadTherapySettings(); }
  }, [token, user, user?.isOnboarded]);


  // --- Initial Setup Effect ---
  useEffect(() => {
    const setupChat = async () => {
      if (!token || !user) { return; }
      
      console.log('[ChatInterface setupChat] Running with:', {
        'user._id': user._id,
        'user.isOnboarded': user.isOnboarded,
        'chatPhase': chatPhase
      });
      
      // Don't re-initialize if we're already in a chat session
      // Only initialize when chatPhase is 'loading' (initial state)
      // Also check hasInitialized ref to prevent double initialization in React StrictMode
      if (chatPhase !== 'loading' || hasInitialized.current) {
        console.log('[ChatInterface setupChat] Already initialized (chatPhase:', chatPhase, ', hasInitialized:', hasInitialized.current, '), skipping');
        return;
      }

      // Mark as initialized immediately to prevent double execution
      hasInitialized.current = true;
      
      setInitialLoading(true); setError(''); setMessages([]);
      try {
        // Retrieve invitation token from localStorage for flow resolution
        const storedInvitationToken = localStorage.getItem('invitationToken');
        if (storedInvitationToken) {
          setInvitationToken(storedInvitationToken);
          console.log('Retrieved invitation token from localStorage for onboarding');
        }

        // Fetch the personas list for everyone — clients need the persona's
        // avatarImageUrl + roleLabel for PersonaHeader rendering, not just
        // admin/therapist users who use the persona-picker. Without this,
        // clients fell back to a hardcoded { key: 'generalist' } stub which
        // never matched their actual conversation persona, so the persona
        // header rendered initials-fallback instead of the assigned avatar.
        try {
          const fetchedPersonas = await apiService.getTherapistPersonas(token);
          setPersonas(fetchedPersonas && fetchedPersonas.length
            ? fetchedPersonas
            : [{ key: 'generalist', name: 'General AI Therapist' }]);
        } catch (personasErr) {
          console.warn('Failed to fetch personas list, falling back to generalist stub:', personasErr?.message);
          setPersonas([{ key: 'generalist', name: 'General AI Therapist' }]);
        }
        // Check URL parameters for onboarding override
        const urlParams = new URLSearchParams(window.location.search);
        const shouldStartOnboarding = urlParams.get('onboarding') === 'true';


        // A human_led flow means the intake call is the onboarding. Starting an
        // AI onboarding conversation here would re-ask eligibility, age and
        // trial terms that a clinician has already settled with the client,
        // and would overwrite the persona the therapist approved.
        const humanLedOnboarding = user?.effectiveOnboardingMode === 'human_led';

        if ((!user.isOnboarded || shouldStartOnboarding) && !isAdmin && !isTherapist && !humanLedOnboarding) {
          setChatPhase('onboarding'); setIsLoading(true);
          try {
            const initialResponse = await apiService.sendOnboardingMessage('', [], null, storedInvitationToken);
            if (initialResponse.response) {
              // If history exists (resuming onboarding), use it instead of just the single response
              if (initialResponse.history && initialResponse.history.length > 0) {
                const messages = initialResponse.history.map(msg => ({
                  text: msg.text,
                  senderRole: msg.senderRole || (msg.sender === 'user' ? 'client' : 'ai'),
                  timestamp: msg.timestamp,
                  isOnboarding: msg.isOnboarding,
                  isInternal: msg.isInternal,  // Preserve for client-side filtering (backup)
                  _id: msg._id
                }));
                setMessages(messages);
              } else {
                // New onboarding - just show the initial message
                const initialMessage = { ...initialResponse.response, senderRole: 'ai' };
                setMessages([initialMessage]);
              }
              if (initialResponse.conversationId) { setCurrentConversationId(initialResponse.conversationId); }
            } else { throw new Error("Failed to get initial onboarding message."); }
            setIsNewChat(false); setActiveSession(null); setSessionsEnabled(false);
            setCurrentPersona('onboarding_assistant'); setSelectedPersonaForNewChat('generalist');
          } catch (onboardingErr) {
            // Handle case where user already has active session (stale isOnboarded state)
            // The error object now has onboardingComplete directly attached by api.js
            if (onboardingErr.onboardingComplete) {
              console.log('[ChatInterface] User already has active session, switching to main chat mode');

              // Clear the ?onboarding=true URL parameter to prevent future loops
              const currentUrl = new URL(window.location.href);
              if (currentUrl.searchParams.get('onboarding')) {
                currentUrl.searchParams.delete('onboarding');
                window.history.replaceState({}, '', currentUrl.toString());
                console.log('[ChatInterface] Cleared onboarding URL parameter');
              }

              // Refresh user data to sync isOnboarded state
              if (refreshUserData) {
                await refreshUserData();
              }
              // Switch to main chat and load existing conversation
              setChatPhase('main');
              const historyData = await apiService.getHistory(token);
              if (historyData && historyData.conversationId) {
                setMessages(historyData.messages || []);
                const loadedPersona = historyData.personaUsed || 'generalist';
                setCurrentPersona(loadedPersona); setSelectedPersonaForNewChat(loadedPersona);
                setCurrentConversationId(historyData.conversationId);
                setIsNewChat(false);
                setActiveSession(historyData.activeSession || null);
                setSessionsEnabled(historyData.sessionsEnabled || false);
                setConversationMode(historyData.controlMode || 'ai');
                if (historyData.crisisResources) setCrisisResources(historyData.crisisResources);
              } else {
                setMessages([]); setCurrentPersona('generalist'); setSelectedPersonaForNewChat('generalist');
                setCurrentConversationId(null); setActiveSession(null); setSessionsEnabled(false);
                setIsNewChat(true);
                setConversationMode('ai');
              }
            } else {
              // Re-throw other errors to be caught by outer catch
              throw onboardingErr;
            }
          }
        } else {
          setChatPhase('main');
          const historyData = await apiService.getHistory(token);
          if (historyData && historyData.conversationId) {
            setMessages(historyData.messages || []);
            const loadedPersona = historyData.personaUsed || 'generalist';
            setCurrentPersona(loadedPersona); setSelectedPersonaForNewChat(loadedPersona);
            setCurrentConversationId(historyData.conversationId);
            setIsNewChat(false);
            setActiveSession(historyData.activeSession || null);
            setSessionsEnabled(historyData.sessionsEnabled || false);
            setConversationMode(historyData.controlMode || 'ai');
            if (historyData.crisisResources) setCrisisResources(historyData.crisisResources);
          } else {
            setMessages([]); setCurrentPersona('generalist'); setSelectedPersonaForNewChat('generalist');
            setCurrentConversationId(null); setActiveSession(null); setSessionsEnabled(false);
            setIsNewChat(true);
            setConversationMode('ai');
          }
        }
      } catch (err) {
        console.error('[ChatInterface setupChat] Initialization error:', err);
        setError("Could not initialize chat. Please try refreshing.");
        setChatPhase('error'); 
      } finally { 
        setInitialLoading(false); 
        setIsLoading(false); 
      }
    };
    setupChat();
  }, [token, user, isAdmin, isTherapist, chatPhase]);

  // --- Handle navigation state from dashboard ---
  useEffect(() => {
    if (location.state?.openSessionSummary) {
      // Open session summary modal
      setSessionReviewModalOpen(true);
      // Clear the state to prevent re-opening on refresh
      navigate('/chat', { replace: true, state: {} });
    } else if (location.state?.openCheckInHistory) {
      // Open check-in history modal
      setCheckInHistoryOpen(true);
      // Clear the state to prevent re-opening on refresh
      navigate('/chat', { replace: true, state: {} });
    }
  }, [location, navigate]);

  // --- Auto-scroll and Focus Effects ---
  useEffect(() => {
    // Debounce scroll to prevent double-scroll when multiple messages
    // arrive in sequence (e.g., optimistic update then server response)
    // 300ms covers typical server round-trip time
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  // --- Socket.IO Event Listener for Triage Approval (Onboarding Phase) ---
  useEffect(() => {
    if (socket && user?._id && chatPhase === 'onboarding' && user?.role?.includes('client')) {
      // Join user-specific room for triage approval notifications
      const userRoom = `user_${user._id}`;
      console.log(`[ChatInterface] Joining user room for triage notifications: ${userRoom}`);
      socket.emit('join', userRoom);

      const handleTriageApproved = (data) => {
        console.log('[ChatInterface] Received triage:approved event:', data);

        // Transition from onboarding to main chat phase
        setChatPhase('main');

        // Update conversation state
        if (data.conversationId) {
          setCurrentConversationId(data.conversationId);
        }
        if (data.personaKey) {
          setCurrentPersona(data.personaKey);
          setSelectedPersonaForNewChat(data.personaKey);
        }

        // Add system message about therapist assignment
        const systemMessage = {
          senderRole: 'system',
          text: data.message || "Your therapist has been assigned! You can start chatting now.",
          timestamp: new Date()
        };

        // Add therapist greeting message
        const therapistGreeting = {
          senderRole: 'ai',
          text: `Hello! I'm ${data.personaName || 'your AI therapist'}, and I'll be working with you from now on. I've reviewed your assessment and I'm here to support you. How are you feeling today?`,
          timestamp: new Date(),
          _id: Date.now().toString()
        };

        setMessages(prev => [...prev, systemMessage, therapistGreeting]);

        // Mark as not new chat since we're resuming an existing conversation
        setIsNewChat(false);

        // Clear loading state
        setIsLoading(false);
        setInitialLoading(false);

        // Trigger autoplay for therapist greeting if enabled
        if (autoplayEnabled) {
          setCurrentlyPlayingMessageId(therapistGreeting._id);
        }

        console.log('[ChatInterface] Successfully transitioned from onboarding to active therapy');
      };

      socket.on('triage:approved', handleTriageApproved);

      return () => {
        socket.off('triage:approved', handleTriageApproved);
        socket.emit('leave', userRoom);
      };
    }
  }, [socket, user?._id, chatPhase, user?.role, autoplayEnabled]);

  // --- Socket.IO Event Listeners for Unified Onboarding Lifecycle ---
  useEffect(() => {
    if (socket && currentConversationId && chatPhase === 'onboarding' && user?.role?.includes('client')) {

      const handleFlowCompleted = (data) => {
        if (data.conversationId !== currentConversationId) return;
        console.log('[ChatInterface] Received onboarding:flow_completed', data);

        // Clear invitation token
        localStorage.removeItem('invitationToken');
        setInvitationToken(null);

        // Check for redirect
        if (data.redirectUrl) {
          navigate(data.redirectUrl);
          return;
        }

        // Transition to main chat
        setChatPhase('main');
        setIsNewChat(false);
        setIsLoading(false);

        // Refresh user data to update isOnboarded
        if (refreshUserData) {
          refreshUserData();
        }
      };

      const handleStageAdvanced = (data) => {
        if (data.conversationId !== currentConversationId) return;
        console.log('[ChatInterface] Received onboarding:stage_advanced', data);
        // Stage transitions are handled via new_message events for the
        // transition/greeting messages. This event is informational.
      };

      const handlePendingReview = (data) => {
        if (data.conversationId !== currentConversationId) return;
        console.log('[ChatInterface] Received onboarding:pending_review', data);

        if (data.message) {
          setMessages(prev => [...prev, {
            senderRole: 'ai',
            text: data.message,
            timestamp: new Date()
          }]);
        }
        setIsLoading(false);
      };

      socket.on('onboarding:flow_completed', handleFlowCompleted);
      socket.on('onboarding:stage_advanced', handleStageAdvanced);
      socket.on('onboarding:pending_review', handlePendingReview);

      return () => {
        socket.off('onboarding:flow_completed', handleFlowCompleted);
        socket.off('onboarding:stage_advanced', handleStageAdvanced);
        socket.off('onboarding:pending_review', handlePendingReview);
      };
    }
  }, [socket, currentConversationId, chatPhase, user?.role, navigate, refreshUserData]);

  // --- Socket.IO Event Listeners ---
  // Note: Includes 'onboarding' phase to receive AI responses after assessment completion
  // Server emits 'new_message' after assessment completion (assessmentController.js:716)
  useEffect(() => {
    if (socket && currentConversationId && (chatPhase === 'main' || chatPhase === 'onboarding') && user?.role?.includes('client')) {
      socket.emit('client:join_conversation_room', currentConversationId);
      const handleNewMessage = (data) => {
        if (data.conversationId === currentConversationId) {
          if (data.message && (data.message.senderRole === 'ai' || data.message.senderRole === 'human_therapist')) {
            setIsLoading(false);
            // Clear the fallback poll timer — socket delivered successfully
            if (missedMessagePollRef.current) {
              clearTimeout(missedMessagePollRef.current);
              missedMessagePollRef.current = null;
            }
          }
          
          // Autoplay new therapist messages if enabled — but never while
          // Advanced Voice Mode is active (its transcripts are already spoken
          // live; queuing TTS here surfaces on voice-mode exit, ST-20260623-0007).
          // Read the ref, not the captured state, to avoid a stale closure.
          if (autoplayEnabled && !voiceModeActiveRef.current && data.message &&
              (data.message.senderRole === 'ai' || data.message.senderRole === 'human_therapist')) {
            const playId = data.message._id || Date.now().toString();
            console.log('Setting autoplay for message:', playId, 'Message:', data.message);
            setCurrentlyPlayingMessageId(playId);
          }
          
          setMessages(prevMessages => {
            const messageExists = prevMessages.some(msg => {
  // If the incoming message has an ID, and an existing message has the same ID, it's a duplicate.
  if (data.message._id && msg._id && msg._id === data.message._id) {
    return true;
  }
  // If the incoming message is from the client AND an existing optimistic message (no _id yet)
  // matches text and sender, and their timestamps are very close, consider it a duplicate.
  // This handles the case where the client's own message is echoed back from the server.
  if (data.message.senderRole === 'client' && msg.senderRole === 'client' && !msg._id) {
    const textMatch = msg.text === data.message.text;
    // Compare timestamps by converting to time value; allow a small tolerance (e.g., 2 seconds)
    const timeDiff = Math.abs(new Date(msg.timestamp).getTime() - new Date(data.message.timestamp).getTime());
    if (textMatch && timeDiff < 2000) { // 2000ms tolerance
      return true;
    }
  }
  // Check for AI/therapist message duplicates (safety for assessment completion responses)
  // This prevents duplicate display if both socket and HTTP mechanisms trigger
  if ((data.message.senderRole === 'ai' || data.message.senderRole === 'human_therapist') &&
      (msg.senderRole === 'ai' || msg.senderRole === 'human_therapist')) {
    const textMatch = msg.text === data.message.text;
    const timeDiff = Math.abs(new Date(msg.timestamp).getTime() - new Date(data.message.timestamp).getTime());
    if (textMatch && timeDiff < 5000) { // 5 second tolerance for AI messages
      console.log('[ChatInterface] Ignoring duplicate AI/therapist message');
      return true;
    }
  }
  return false;
});

            if (messageExists) return prevMessages;
            return [...prevMessages, data.message];
          });
        }
      };
      const handleModeChange = (data) => {
        if (data.conversationId === currentConversationId) {
          // Update conversation mode state when mode changes
          if (data.newMode) {
            setConversationMode(data.newMode);
          }
          if (data.systemMessage) {
            setMessages(prevMessages => {
                const messageExists = prevMessages.some(msg => msg.text === data.systemMessage.text && msg.senderRole === 'system' && (msg.timestamp === data.systemMessage.timestamp || msg.text.includes(data.newMode.toUpperCase())));
                if(messageExists) return prevMessages;
                return [...prevMessages, data.systemMessage];
            });
            // Clear loading state when mode changes to human or assisted
            // (therapist will handle the conversation in both cases)
            if (data.newMode === 'human' || data.newMode === 'assisted') {
              // Use setTimeout to ensure this runs after the current update cycle
              setTimeout(() => setIsLoading(false), 0);
            }
          }
        }
      };
      socket.on('new_message', handleNewMessage);
      socket.on('control_mode_changed', handleModeChange);
      const handleTherapistGoalConfirmation = (data) => {
        goalDialogsRef.current?.handleTherapistGoalConfirmation(data);
      };
      socket.on('therapist_initiated_goal_confirmation', handleTherapistGoalConfirmation);
      
      const handleTherapistAssessmentRequest = (data) => {
        // If voice mode is active, end the session — assessments take too long
        if (voiceLivePanelRef.current?.isActive()) {
          console.log('[ChatInterface] Assessment requested during voice mode — ending voice session');
          voiceLivePanelRef.current.end();
          setVoiceModeActive(false);
        }
        assessmentManagerRef.current?.handleTherapistAssessmentRequest(data);
      };
      socket.on('therapist_assessment_request', handleTherapistAssessmentRequest);

      // Handle function calls from voice mode (goals, assessments)
      const handleVoiceFunctionCall = (data) => {
        console.log('[ChatInterface] Voice function call:', data.type);
        if (data.type === 'goal' && data.goalDescription) {
          // Pause voice while goal dialog is shown
          voiceLivePanelRef.current?.pause();
          goalDialogsRef.current?.showGoalSuggestion({
            description: data.goalDescription,
            goalType: data.goalType || 'therapeutic'
          });
        }
      };
      socket.on('server:voice:function_call', handleVoiceFunctionCall);

      // Handle mode change during voice — end voice session
      const handleVoiceModeChange = (data) => {
        console.log('[ChatInterface] Voice mode change suggested:', data.targetMode);
        if (data.targetMode !== 'ai') {
          // End voice session — voice mode only works in AI mode
          voiceLivePanelRef.current?.end();
          setVoiceModeActive(false);
        }
      };
      socket.on('server:voice:mode_change_suggested', handleVoiceModeChange);

      // Handle conversation deletion by therapist
      const handleConversationDeleted = (data) => {
        console.log('[ChatInterface] Conversation deleted by therapist:', data);
        if (data.conversationId === currentConversationId) {
          // Show notification if provided
          if (data.message) {
            console.log('[ChatInterface] Deletion message:', data.message);
            // Could show an alert or use a notification system here
          }
          // Reload to get the next available conversation
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      };
      socket.on('conversation_deleted', handleConversationDeleted);

      // Gen 2 processing status is subscribed in its own effect below, which is
      // not gated on currentConversationId (ST-20260916-0004).

      // Adult-9: persistent crisis-resource banner
      const handleCrisisResourcesActivated = (data) => {
        console.log('[ChatInterface] crisis_resources_activated:', data);
        setCrisisResources({
          resources: data.resources,
          locale: data.locale,
          activatedReason: data.activatedReason,
          activatedAt: data.activatedAt,
        });
        // Re-show banner even if user previously dismissed it — server only
        // re-emits on a new (or more severe) activation, so this is intentional.
        setCrisisBannerDismissed(false);
        setCrisisBannerCollapsed(false);
      };
      socket.on('crisis_resources_activated', handleCrisisResourcesActivated);

      return () => {
        if (socket && currentConversationId) {
            socket.emit('client:leave_conversation_room', currentConversationId);
            socket.off('new_message', handleNewMessage);
            socket.off('control_mode_changed', handleModeChange);
            socket.off('conversation_deleted', handleConversationDeleted);
            socket.off('therapist_initiated_goal_confirmation', handleTherapistGoalConfirmation);
            socket.off('therapist_assessment_request', handleTherapistAssessmentRequest);
            socket.off('server:voice:function_call', handleVoiceFunctionCall);
            socket.off('server:voice:mode_change_suggested', handleVoiceModeChange);
            socket.off('crisis_resources_activated', handleCrisisResourcesActivated);
        }
      };
    }
  }, [socket, currentConversationId, chatPhase, user?._id, user?.role]);

  // Gen 2 processing status (thinking, consulting, formulating, done).
  //
  // Deliberately NOT gated on currentConversationId. During the opening message
  // the client does not have a conversation id yet: it arrives in the awaited
  // response from the same request the generation runs inside, so the join to
  // conversation_<id> cannot have happened and every opening status was emitted
  // to a room with nobody in it (ST-20260916-0004). The server now also sends
  // these to the client's personal room, which the socket joins at connect, so
  // this listener catches them before the conversation is known.
  useEffect(() => {
    if (!socket || !user?.role?.includes('client')) return;

    const handleProcessingStatus = (data) => {
      // Once we know which conversation we are in, ignore status for any other.
      // Before that (the opening) accept it: the personal room is ours alone.
      if (data?.conversationId && currentConversationId &&
          String(data.conversationId) !== String(currentConversationId)) {
        return;
      }
      if (data?.phase === 'done') {
        setProcessingStatus(null);
      } else {
        setProcessingStatus(data?.message || null);
      }
    };

    socket.on('ai:processing_status', handleProcessingStatus);
    return () => socket.off('ai:processing_status', handleProcessingStatus);
  }, [socket, currentConversationId, user?.role]);


  // --- Send Message Handler ---
  const handleSendMessage = useCallback(async (e, messageTextOverride = null) => {
    e.preventDefault();
    const textToSend = messageTextOverride || inputText;
    if (!textToSend.trim() || isLoading || initialLoading || chatPhase === 'loading' || chatPhase === 'error') return;
    const clientMessageText = textToSend;
    const clientTimestamp = new Date();
    const clientMessageForState = { senderRole: 'client', text: clientMessageText, timestamp: clientTimestamp };
    setMessages(prevMessages => [...prevMessages, clientMessageForState]);
    // Only clear inputText if we're not using an override
    if (!messageTextOverride) {
      setInputText('');
    }
    setIsLoading(true);
    setError('');

    // Check if unified message endpoint is enabled
    const useUnifiedEndpoint = platformSettings?.onboardingConfiguration?.useUnifiedMessageEndpoint === true;

    try {
      if (chatPhase === 'onboarding' && !useUnifiedEndpoint) {
        // ===== LEGACY ONBOARDING PATH (flag OFF) =====
        let conversationIdToUse = currentConversationId;
        const response = await apiService.sendOnboardingMessage(clientMessageText, messages, conversationIdToUse, invitationToken, { inputMethod: lastInputMethodRef.current });
        lastInputMethodRef.current = 'text'; // Reset after successful send
        if (response.conversationId && !currentConversationId) { setCurrentConversationId(response.conversationId); }
        if (response.onboardingComplete) {
          localStorage.removeItem('invitationToken');
          setInvitationToken(null);
          if (response.postCompletionRedirect) {
            setIsLoading(false);
            navigate(response.postCompletionRedirect);
            return;
          }
          const finalMessages = [];
          if (response.finalOnboardingMessage) finalMessages.push({ ...response.finalOnboardingMessage, senderRole: response.finalOnboardingMessage.sender === 'ai' ? 'ai' : 'system', isOnboarding: true });
          if (response.firstTherapistMessage) finalMessages.push({ ...response.firstTherapistMessage, senderRole: 'ai', isOnboarding: false });
          setMessages(prev => [...prev, ...finalMessages]);
          setChatPhase('main'); setCurrentPersona('generalist'); setIsNewChat(false);
          if (response.conversationId) { setCurrentConversationId(response.conversationId); }
          if (refreshUserData) { await refreshUserData(); }
          setIsLoading(false);
        } else if (response.suggestionType === 'assessment' && response.assessmentDetails) {
          if (response.originalAiText) {
            const aiAccompanyingText = { senderRole: 'ai', text: response.originalAiText, timestamp: new Date(), isOnboarding: response.currentStage === 1 };
            setMessages(prevMessages => [...prevMessages, aiAccompanyingText]);
          }
          if (assessmentManagerRef.current?.launchAssessment) {
            assessmentManagerRef.current.launchAssessment(
              response.assessmentDetails.testKey, response.assessmentDetails.testName,
              response.assessmentDetails.testDescription, response.assessmentDetails.initiatedBy || 'ai'
            );
          }
          setIsLoading(false);
        } else if (response.response) {
          const aiMessage = {
            senderRole: 'ai', text: response.response.text, timestamp: new Date(),
            _id: Date.now().toString(), isOnboarding: response.currentStage === 1
          };
          setMessages(prevMessages => [...prevMessages, aiMessage]);
          if (autoplayEnabled) { setCurrentlyPlayingMessageId(aiMessage._id); }
          setIsLoading(false);
        } else if (response.requiresReview) {
          const pendingMessage = {
            senderRole: 'ai',
            text: response.pendingMessage || 'Thank you for completing the assessment. Your responses are being reviewed to ensure we match you with the right support. You\'ll be notified soon.',
            timestamp: new Date(), _id: Date.now().toString(), isOnboarding: response.currentStage === 1
          };
          setMessages(prevMessages => [...prevMessages, pendingMessage]);
          setIsLoading(false);
        } else { throw new Error('Invalid response received during onboarding.'); }

      } else {
        // ===== UNIFIED PATH (both onboarding with flag ON and main chat) =====
        let personaToSend = (chatPhase === 'main' && isNewChat) ? selectedPersonaForNewChat : null;
        const messageData = {
          message: clientMessageText,
          persona: personaToSend,
          startNew: chatPhase === 'main' && isNewChat,
          conversationId: (chatPhase === 'main' && isNewChat) ? null : currentConversationId,
          clientTimestamp: clientTimestamp.toISOString(),
          inputMethod: lastInputMethodRef.current
        };

        // For onboarding conversations using unified endpoint, include invitationToken
        if (chatPhase === 'onboarding' && invitationToken) {
          messageData.invitationToken = invitationToken;
        }

        const data = await apiService.sendMessage(messageData, token);
        lastInputMethodRef.current = 'text'; // Reset after successful send

        if (data) {
            if (data.conversationId && (isNewChat || !currentConversationId)) { setCurrentConversationId(data.conversationId); }
            if (chatPhase === 'main' && isNewChat) { setCurrentPersona(selectedPersonaForNewChat); setIsNewChat(false); }
            if (data.activeSession !== undefined) setActiveSession(data.activeSession);
            if (data.sessionsEnabled !== undefined) setSessionsEnabled(data.sessionsEnabled);

            if (data.suggestionType === 'goal' && data.goalDescription) {
                if (data.originalAiText) {
                    const aiAccompanyingText = {
                        senderRole: 'ai',
                        text: data.originalAiText,
                        timestamp: new Date(),
                        _id: Date.now().toString()
                    };
                    setMessages(prevMessages => {
                        const messageExists = prevMessages.some(msg => msg.text === aiAccompanyingText.text && msg.timestamp === aiAccompanyingText.timestamp);
                        if (messageExists) return prevMessages;
                        return [...prevMessages, aiAccompanyingText];
                    });
                    if (autoplayEnabled) { setCurrentlyPlayingMessageId(aiAccompanyingText._id); }
                }
                goalDialogsRef.current?.showGoalSuggestion({
                    description: data.goalDescription,
                    goalType: data.goalType,
                    originalAiText: data.originalAiText,
                    originalFunctionCall: data.originalFunctionCall
                });
                setIsLoading(false);
            } else if (data.suggestionType === 'assessment' && data.assessmentDetails) {
                console.log('ChatInterface: Received assessment suggestion:', data);
                 if (data.originalAiText) {
                    const aiAccompanyingText = {
                        senderRole: 'ai',
                        text: data.originalAiText,
                        timestamp: new Date(),
                        _id: Date.now().toString()
                    };
                    setMessages(prevMessages => {
                        const messageExists = prevMessages.some(msg => msg.text === aiAccompanyingText.text && msg.timestamp === aiAccompanyingText.timestamp);
                        if (messageExists) return prevMessages;
                        return [...prevMessages, aiAccompanyingText];
                    });
                    if (autoplayEnabled) { setCurrentlyPlayingMessageId(aiAccompanyingText._id); }
                }
                assessmentManagerRef.current?.launchAssessment(
                    data.assessmentDetails.testKey,
                    data.assessmentDetails.testName,
                    data.assessmentDetails.testDescription,
                    data.assessmentDetails.initiatedBy || 'ai'
                );
                setIsLoading(false);
            } else if (data.message && data.message === "Message received, AI is processing.") {
                // Acknowledgment — AI response will arrive via WebSocket. isLoading remains true.
                console.log("ChatInterface: Received acknowledgment from server. Waiting for AI reply via WebSocket.");
                // Fallback: if socket doesn't deliver within 15s (e.g., deployment transition,
                // multi-instance routing), poll the API for missed messages.
                if (missedMessagePollRef.current) clearTimeout(missedMessagePollRef.current);
                // Bounded, RETRYING fallback poll. The previous one-shot 15s poll
                // gave up before slow safety-turn responses landed (David v8: a
                // 26s response + a dropped socket → silence after a suicide-plan
                // disclosure). Retry until the message appears or the cap is hit,
                // and include `system` messages so the human-handoff confirmation
                // (a system message) is also recovered. handleNewMessage clears
                // this timer if the socket delivers, so a healthy socket stops it.
                const POLL_INTERVAL_MS = 13000;
                const MAX_POLL_ATTEMPTS = 6; // ~78s — covers long turns + reconnects
                let pollAttempts = 0;
                const runMissedPoll = async () => {
                  pollAttempts++;
                  try {
                    const convData = await apiService.getHistory(token);
                    const serverMessages = convData?.messages || [];
                    if (serverMessages.length > 0) {
                      const clientMsgTime = clientTimestamp.getTime();
                      const missedMessages = serverMessages.filter(m =>
                        (m.senderRole === 'ai' || m.senderRole === 'human_therapist' || m.senderRole === 'system') &&
                        new Date(m.timestamp).getTime() > clientMsgTime
                      );
                      if (missedMessages.length > 0) {
                        console.log(`[ChatInterface] Fallback poll recovered ${missedMessages.length} missed message(s) on attempt ${pollAttempts}`);
                        setMessages(prev => {
                          const existingIds = new Set(prev.filter(m => m._id).map(m => m._id.toString()));
                          const newMsgs = missedMessages.filter(m => m._id && !existingIds.has(m._id.toString()));
                          return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
                        });
                        setIsLoading(false);
                        setProcessingStatus(null);
                        missedMessagePollRef.current = null;
                        return; // recovered — stop retrying
                      }
                    }
                  } catch (pollErr) {
                    console.warn(`[ChatInterface] Fallback poll attempt ${pollAttempts} failed:`, pollErr.message);
                  }
                  // Nothing yet (response may still be generating) — retry until the cap.
                  if (pollAttempts < MAX_POLL_ATTEMPTS) {
                    missedMessagePollRef.current = setTimeout(runMissedPoll, POLL_INTERVAL_MS);
                  } else {
                    missedMessagePollRef.current = null;
                  }
                };
                missedMessagePollRef.current = setTimeout(runMissedPoll, POLL_INTERVAL_MS);
            } else if (data.acknowledged && data.processing) {
                // Server's concurrent processing guard is active — a previous message is still being processed.
                // The AI response will arrive via WebSocket when complete. Keep isLoading true but add a safety timeout.
                console.log("ChatInterface: Server processing previous message. Waiting for socket response.");
                setTimeout(() => {
                    setIsLoading(prev => {
                        if (prev) {
                            console.warn("ChatInterface: Timed out waiting for AI response after concurrent block");
                            setError("Your message was received but the response is taking longer than expected. Please try again in a moment.");
                            return false;
                        }
                        return prev;
                    });
                }, 120000); // 2 min — matches server lock TTL
            } else if (data.message && (
                data.status === 'human_involved' ||
                data.status === 'human_review_requested' || // AI still responding while a human is being summoned (post-handoff safety continuity)
                data.controlMode === 'assisted' ||
                data.controlMode === 'human'
            )) {
                 console.log("ChatInterface: Message sent, human/handoff mode. controlMode:", data.controlMode, "status:", data.status);
                 setIsLoading(false);
            } else if (data.message) {
                // Defensive: any response carrying a message clears isLoading.
                console.log("ChatInterface: Received message response, controlMode:", data.controlMode, "status:", data.status, "message:", data.message);
                setIsLoading(false);
            } else if (data.modeChanged || data.aiTextAlreadyDelivered || data.handled === true) {
                // Function-call response shape (e.g. suggest_mode_change handoff):
                // server returns { handled, modeChanged, aiMessage, systemMessage,
                // aiTextAlreadyDelivered } with no top-level `message` field. The
                // AI text and the system handoff marker arrive via the messages array
                // (or socket) — the HTTP response just confirms the function call
                // was processed. Clear isLoading so the input accepts the next message.
                console.log("ChatInterface: Function-call response received (modeChanged=" + !!data.modeChanged + ", aiTextAlreadyDelivered=" + !!data.aiTextAlreadyDelivered + ") — clearing isLoading.");
                setIsLoading(false);
            } else {
                // Truly unknown response shape — clear isLoading defensively rather than
                // leaving the input stuck. Log so we can investigate any unfamiliar shape.
                console.warn("ChatInterface: Unrecognised response shape — clearing isLoading defensively. Keys:", Object.keys(data));
                setIsLoading(false);
            }
        } else {
             throw new Error('Invalid or empty response received from server after sending message.');
        }
      }
    } catch (err) {
      setError(err.message || `Failed to get reply during ${chatPhase} phase.`);
      setIsLoading(false);
    }
  }, [inputText, isLoading, initialLoading, chatPhase, token, currentPersona, isNewChat, selectedPersonaForNewChat, currentConversationId, messages, autoplayEnabled, invitationToken, platformSettings]);

  // --- Goal Suggestion Confirmation Handlers ---


  const triggerAIAfterGoalAction = useCallback(async (actionContext, options = {}) => {
      setIsLoading(true); setError('');

      try {
          // Handle differently based on chat phase
          if (chatPhase === 'onboarding') {
              // For onboarding, send as a regular message through onboarding API
              const response = await apiService.sendOnboardingMessage(actionContext, messages, currentConversationId, invitationToken, options);

              if (response.conversationId && !currentConversationId) {
                  setCurrentConversationId(response.conversationId);
              }

              if (response.onboardingComplete) {
                  // Clear invitation token from localStorage after onboarding completes
                  localStorage.removeItem('invitationToken');
                  setInvitationToken(null);
                  console.log('Cleared invitation token after onboarding completion');

                  // Check for post-completion redirect (e.g., parent dashboard for parent flows)
                  if (response.postCompletionRedirect) {
                      console.log('Post-completion redirect:', response.postCompletionRedirect);
                      setIsLoading(false);
                      navigate(response.postCompletionRedirect);
                      return;
                  }

                  // Handle onboarding completion
                  const finalMessages = [];
                  if (response.finalOnboardingMessage) finalMessages.push({ ...response.finalOnboardingMessage, senderRole: response.finalOnboardingMessage.sender === 'ai' ? 'ai' : 'system', isOnboarding: true });
                  if (response.firstTherapistMessage) finalMessages.push({ ...response.firstTherapistMessage, senderRole: 'ai', isOnboarding: false });
                  setMessages(prev => [...prev, ...finalMessages]);
                  setChatPhase('main'); setCurrentPersona('generalist'); setIsNewChat(false);
                  // Refresh user data to update isOnboarded status
                  if (refreshUserData) {
                      await refreshUserData();
                  }
                  setIsLoading(false);
              } else if (response.response) {
                  // Add the AI response directly if it's included
                  const aiMessage = {
                      senderRole: 'ai',
                      text: response.response.text,
                      timestamp: new Date(),
                      _id: Date.now().toString() // Add temporary ID for autoplay
                  };
                  setMessages(prev => [...prev, aiMessage]);
                  // Trigger autoplay if enabled
                  if (autoplayEnabled) {
                      setCurrentlyPlayingMessageId(aiMessage._id);
                  }
                  setIsLoading(false);
              } else {
                  setIsLoading(false);
              }
          } else {
              // Main chat phase - use existing logic
              // Mark goal action messages as internal with proper context for the LLM
              const isGoalDecline = actionContext === "Goal suggestion declined.";
              const enrichedMessage = isGoalDecline
                ? "[SYSTEM: The client declined the AI's goal suggestion via the UI confirmation dialog. Acknowledge their choice briefly and respectfully, then continue the conversation naturally. Do not re-suggest the same goal.]"
                : actionContext;
              const messageData = { message: enrichedMessage, persona: null, startNew: false, conversationId: currentConversationId, isInternal: true };
              const data = await apiService.sendMessage(messageData, token);
              
              if (data && data.suggestionType === 'goal') { 
                  if (data.originalAiText) { const aiAccompanyingText = { senderRole: 'ai', text: data.originalAiText, timestamp: new Date() }; setMessages(prevMessages => [...prevMessages, aiAccompanyingText]); }
                  goalDialogsRef.current?.showGoalSuggestion({ 
                      description: data.goalDescription, 
                      goalType: data.goalType,
                      originalAiText: data.originalAiText, 
                      originalFunctionCall: data.originalFunctionCall 
                  });
                  setIsLoading(false); 
              } else if (data && data.message === "Message received, AI is processing.") {
                  // isLoading remains true.
              } else {
                  console.warn("ChatInterface: Unexpected response from AI after goal action or no reply field.");
                  setIsLoading(false); 
              }
          }
      } catch (err) {
          // Check if this is an "already onboarded" error - handle gracefully
          if (err.onboardingComplete) {
            console.log('[ChatInterface triggerAIAfterGoalAction] Onboarding already complete, switching to main chat');
            setChatPhase('main');
            // Clear the ?onboarding=true URL parameter to prevent future loops
            const currentUrl = new URL(window.location.href);
            if (currentUrl.searchParams.get('onboarding')) {
              currentUrl.searchParams.delete('onboarding');
              window.history.replaceState({}, '', currentUrl.toString());
            }
          } else {
            setError(err.message || `Failed to get AI follow-up reply.`);
          }
          setIsLoading(false);
      }
  }, [token, currentConversationId, chatPhase, messages, invitationToken]);

  // --- Start New Main Chat Handler ---
  const handleStartNewChat = () => {
    if (chatPhase !== 'main') return; 
    setMessages([]); setCurrentPersona(selectedPersonaForNewChat); setIsNewChat(true); 
    setError(''); setCurrentConversationId(null); setActiveSession(null); setSessionsEnabled(false); setSummaryStatus('');
  };

  const addSystemMessage = (message) => {
    setMessages(prev => [...prev, message]);
  };

  const handleOpenGoalModal = () => goalDialogsRef.current?.openGoalModal();
  const handleOpenCheckInModal = () => setCheckInOpen(true);
  const handleCloseCheckInModal = () => setCheckInOpen(false);
  const handleOpenCheckInHistoryModal = () => setCheckInHistoryOpen(true);
  const handleCloseCheckInHistoryModal = () => setCheckInHistoryOpen(false);
  const handleOpenJournalModal = () => setJournalModalOpen(true);
  const handleCloseJournalModal = () => setJournalModalOpen(false);
  const handleOpenSessionHistoryModal = () => setSessionHistoryOpen(true);
  const handleCloseSessionHistoryModal = () => setSessionHistoryOpen(false);

  const handleSessionUpdate = (sessionData) => { if (sessionData.hasOwnProperty('activeSession')) { setActiveSession(sessionData.activeSession); } if (sessionData.hasOwnProperty('sessionsEnabledStatus')) { setSessionsEnabled(sessionData.sessionsEnabledStatus); } };
  const fetchSessionReview = useCallback(async () => { if (!currentConversationId || !token) { setSessionReviewError("No active conversation selected."); return; } setSessionReviewLoading(true); setSessionReviewError(''); setClientSummaryData(null); try { const data = await apiService.getClientSessionSummary(currentConversationId, token); setClientSummaryData(data); } catch (err) { const errorMsg = err.response?.data?.message || err.message || 'Failed to load session review.'; setSessionReviewError(errorMsg); if (err.response?.status === 404 && err.response?.data?.message?.includes('not yet generated')) { setSessionReviewError('Summary and prompts have not been generated for this session yet.'); } } finally { setSessionReviewLoading(false); } }, [currentConversationId, token]);
  const handleOpenSessionReviewModal = () => { setSessionReviewModalOpen(true); fetchSessionReview(); };
  const handleCloseSessionReviewModal = () => { setSessionReviewModalOpen(false); setClientSummaryData(null); setSessionReviewError(''); setSessionReviewLoading(false); };
  const handleOpenProgressModal = () => setIsProgressModalOpen(true);
  const handleCloseProgressModal = () => setIsProgressModalOpen(false);
  const handleOpenSupportTicket = () => setSupportTicketOpen(true);
  const handleCloseSupportTicket = () => setSupportTicketOpen(false);
  const handleRequestReview = async () => { 
    console.log('handleRequestReview called - conversationId:', currentConversationId, 'chatPhase:', chatPhase);
    if (!currentConversationId || chatPhase !== 'main') {
      console.log('Review request blocked - missing conversationId or wrong phase');
      return; 
    }
    // Open the modal instead of sending request immediately
    setReviewRequestModalOpen(true);
  };
  
  const handleConfirmReviewRequest = async (message) => {
    setReviewRequestStatus({ loading: true, error: '', success: '' }); 
    try { 
      console.log('Calling API requestReview for conversation:', currentConversationId);
      const result = await apiService.requestReview(currentConversationId, token, message); 
      console.log('Review request result:', result);
      setReviewRequestModalOpen(false);
      setReviewRequestStatus({ loading: false, error: '', success: result.msg || 'Review requested successfully.' }); 
      // Update rate limit info if returned
      if (result.rateLimitInfo) {
        setReviewRateLimitInfo(result.rateLimitInfo);
      }
      setTimeout(() => setReviewRequestStatus(prev => ({ ...prev, success: '' })), 5000); 
    } catch (err) { 
      console.error('Review request error:', err);
      // Check if it's a rate limit error
      if (err.response?.status === 429 && err.response?.data?.rateLimitInfo) {
        setReviewRateLimitInfo(err.response.data.rateLimitInfo);
        setReviewRequestStatus({ loading: false, error: err.response.data.msg || 'You can only request a review once per day.', success: '' }); 
      } else {
        setReviewRequestStatus({ loading: false, error: err.message || 'Failed to request review.', success: '' }); 
      }
      setTimeout(() => setReviewRequestStatus(prev => ({ ...prev, error: '' })), 5000); 
    } 
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex', flexDirection: 'column', height: 'calc(100vh - 150px)', minHeight: '400px', maxHeight: isMobile ? '100vh' : '700px', overflow: 'hidden',
        // ST-20261006-0001: on iPhone browsers 100vh is the height with the toolbars
        // hidden, so the card overflowed the visible screen and its top slid under the
        // fixed header. dvh is the height actually visible; 100vh stays as the fallback.
        '@supports (height: 100dvh)': { height: 'calc(100dvh - 150px)', maxHeight: isMobile ? '100dvh' : '700px' }
      }}
      data-testid="client-dashboard"
    >
      <Box sx={{ position: 'fixed', top: 70, right: 20, zIndex: 1000, backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: '4px', padding: '4px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', displayPrint: 'none' }}>
        {isTherapist && ( <Button variant="contained" color="secondary" size="small" onClick={() => navigate('/therapist/dashboard')} startIcon={<SupervisorAccountIcon />} sx={{ mr: isAdmin ? 1 : 0 }}> Therapist Dashboard </Button> )}
        {isAdmin && ( <Button variant="contained" color="info" size="small" onClick={() => navigate('/admin/dashboard')} startIcon={<SupervisorAccountIcon />}> Admin Dashboard </Button> )}
      </Box>
      {/* Persistent persona indicator — visible during onboarding AND main chat, on web AND mobile.
          ChatControls (below) is hidden during onboarding and on mobile, so the avatar render
          there isn't reliable on its own. */}
      {chatPhase !== 'loading' && (
        <PersonaHeader
          persona={personas.find(p => p.key === currentPersona)}
          currentPersonaKey={currentPersona}
          talkModeEnabled={autoplayEnabled}
          onToggleTalkMode={(next) => {
            setAutoplayEnabled(next);
            apiService.logActivityEvent('AUTOPLAY_TOGGLED', { enabled: next }, currentConversationId, token);
          }}
          subtitle={
            clientUiSettings?.chatDisclaimerFooter?.enabled && clientUiSettings?.chatDisclaimerFooter?.text
              ? clientUiSettings.chatDisclaimerFooter.text
              : null
          }
          onChangeAppearance={
            clientUiSettings?.representationsEnabled
              ? () => setRepresentationPickerOpen(true)
              : null
          }
          rightAccessory={
            (!isMobile && chatPhase === 'main') ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<SupervisorAccountIcon />}
                  onClick={handleRequestReview}
                  disabled={!currentConversationId || reviewRequestStatus.loading}
                  sx={{
                    textTransform: 'none', fontWeight: 600,
                    background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' },
                    '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                  }}
                >
                  Request review
                </Button>
                <IconButton size="small" aria-label="More options" onClick={(e) => setChatMoreAnchor(e.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={chatMoreAnchor} open={Boolean(chatMoreAnchor)} onClose={() => setChatMoreAnchor(null)}>
                  <MenuItem onClick={() => { setChatMoreAnchor(null); setVoiceSelectorOpen(true); }}>
                    <ListItemIcon><RecordVoiceOverIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Choose voice</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setChatMoreAnchor(null); handleOpenSupportTicket(); }}>
                    <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Get help</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            ) : null
          }
        />
      )}
      {/* Voice picker dialog (opened from the merged header's ⋯ menu). Rendered
          unconditionally — visibility is controlled by `open`. */}
      <ClientVoiceSelector
        token={token}
        open={voiceSelectorOpen}
        onClose={() => setVoiceSelectorOpen(false)}
        onVoiceChanged={() => { if (typeof fetchTherapistVoiceSettings === 'function') fetchTherapistVoiceSettings(); }}
      />
      <RepresentationPicker
        open={representationPickerOpen}
        onClose={() => setRepresentationPickerOpen(false)}
        token={token}
        personaKey={currentPersona}
      />
      {isMobile ? (
        <MobileInterface
          user={user}
          chatPhase={chatPhase}
          initialLoading={initialLoading}
          currentConversationId={currentConversationId}
          sessionsEnabled={sessionsEnabled}
          clientTherapySettings={clientTherapySettings}
          handlers={{
            handleOpenGoalModal,
            handleOpenSessionHistoryModal,
            handleOpenJournalModal,
            handleOpenCheckInModal,
            handleOpenCheckInHistoryModal,
            handleOpenSessionReviewModal,
            handleOpenProgressModal,
            handleRequestReview,
            handleOpenSupportTicket
          }}
          reviewRequestStatus={reviewRequestStatus}
          showHeader={false}
          showFeatureMenu={false}
        />
      ) : (
        <ChatControls
          chatPhase={chatPhase}
          initialLoading={initialLoading}
          personas={personas}
          user={user}
          selectedPersonaForNewChat={selectedPersonaForNewChat}
          setSelectedPersonaForNewChat={setSelectedPersonaForNewChat}
          onStartNewChat={handleStartNewChat}
          isNewChat={isNewChat}
          messages={messages}
          currentConversationId={currentConversationId}
          currentPersona={currentPersona}
          sessionsEnabled={sessionsEnabled}
          clientTherapySettings={clientTherapySettings}
          handleRequestReview={handleRequestReview}
          reviewRequestStatus={reviewRequestStatus}
          isMobile={isMobile}
          onOpenSupportTicket={handleOpenSupportTicket}
          token={token}
          onVoiceChanged={fetchTherapistVoiceSettings}
        />
      )}
      {chatPhase === 'main' && (clientTherapySettings?.sessionMode === 'structured' || clientTherapySettings?.sessionMode === 'hybrid') && currentConversationId && ( <SessionControls token={token} conversationId={currentConversationId} onSessionUpdate={handleSessionUpdate} sessionEnabled={sessionsEnabled} activeSession={activeSession} clientSettings={clientTherapySettings} /> )}
      {/* Service Status Banner for Times of Service */}
      <ServiceStatusBanner
        conversationId={currentConversationId}
        socket={socket}
        token={token}
      />
      {/* Adult-9: persistent crisis-resource banner — visible for the rest of
          a session in which SI has been disclosed. */}
      {crisisResources && !crisisBannerDismissed && (
        <CrisisResourcesBanner
          resources={crisisResources.resources}
          locale={crisisResources.locale}
          collapsed={crisisBannerCollapsed}
          onCollapse={() => setCrisisBannerCollapsed(c => !c)}
          onDismiss={async () => {
            setCrisisBannerDismissed(true);
            try {
              if (currentConversationId) {
                await fetch(
                  `${process.env.REACT_APP_API_URL || ''}/api/chat/conversations/${currentConversationId}/crisis-resources/dismiss`,
                  {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );
              }
            } catch (err) {
              console.warn('[ChatInterface] crisis-resources dismiss POST failed:', err);
            }
          }}
        />
      )}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        <MessageList
          messages={messages}
          initialLoading={initialLoading}
          isLoading={isLoading}
          chatPhase={chatPhase}
          conversationMode={conversationMode}
          getVoiceIdForMessage={getVoiceIdForMessage}
          messagesEndRef={messagesEndRef}
          autoplayEnabled={autoplayEnabled && !autoplayPaused}
          currentlyPlayingMessageId={currentlyPlayingMessageId}
          onPlaybackComplete={() => setCurrentlyPlayingMessageId(null)}
          platformSettings={platformSettings}
          therapistSettings={therapistVoiceSettings}
          userSettings={user}
          clientId={user?._id || user?.id}
          setPlayingAudio={setPlayingAudio}
          token={token}
          processingStatus={processingStatus}
          aiAvatarUrl={
            clientUiSettings?.selectedRepresentation?.avatarImageUrl
              || personas.find(p => p.key === currentPersona)?.avatarImageUrl
              || null
          }
          onGoalProposal={(suggestion) => goalDialogsRef.current?.showGoalSuggestion(suggestion)}
          onGoalDecline={() => goalDialogsRef.current?.declineGoalSuggestion()}
          onLaunchAssessment={(testKey, testName, testDescription, initiatedBy) => {
            assessmentManagerRef.current?.launchAssessment(testKey, testName, testDescription, initiatedBy);
          }}
        />
      </Box>
      {/* The AI-assistant disclaimer now appears as the persona-header subtitle
          (v2) instead of a separate footer row. */}
      {error && <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>{error}</Alert>}
      {reviewRequestStatus.error && <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>{reviewRequestStatus.error}</Alert>}
      {reviewRequestStatus.success && <Alert severity="success" sx={{ m: 1, flexShrink: 0 }}>{reviewRequestStatus.success}</Alert>}
      {isMobile && (
        <MobileInterface
          user={user}
          chatPhase={chatPhase}
          initialLoading={initialLoading}
          currentConversationId={currentConversationId}
          sessionsEnabled={sessionsEnabled}
          clientTherapySettings={clientTherapySettings}
          handlers={{
            handleOpenGoalModal,
            handleOpenSessionHistoryModal,
            handleOpenJournalModal,
            handleOpenCheckInModal,
            handleOpenCheckInHistoryModal,
            handleOpenSessionReviewModal,
            handleOpenProgressModal,
            handleRequestReview,
            handleOpenSupportTicket
          }}
          reviewRequestStatus={reviewRequestStatus}
          showHeader={false}
          showFeatureMenu={true}
        />
      )}
      {/* Show VoiceLivePanel instead of ChatInput when voice mode is active */}
      {voiceModeActive && socket && currentConversationId ? (
        <VoiceLivePanel
          ref={voiceLivePanelRef}
          socket={socket}
          conversationId={currentConversationId}
          onClose={() => {
            setVoiceModeActive(false);
            // Cancel any playback queued during the session so a transcript
            // can't autoplay as the panel unmounts (ST-20260623-0007).
            setCurrentlyPlayingMessageId(null);
          }}
        />
      ) : (
        <ChatInput
          inputText={inputText}
          setInputText={setInputText}
          onSubmit={handleSendMessage}
          isLoading={isLoading}
          initialLoading={initialLoading}
          chatPhase={chatPhase}
          autoplayEnabled={autoplayEnabled}
          onAutoplayToggle={() => {
            const newState = !autoplayEnabled;
            setAutoplayEnabled(newState);
            apiService.logActivityEvent('AUTOPLAY_TOGGLED', { enabled: newState }, currentConversationId, token);
          }}
          onVoiceInput={() => { lastInputMethodRef.current = 'voice'; }}
          onListeningStart={handleSttListeningStart}
          onListeningEnd={handleSttListeningEnd}
          onSpeechError={(errorType) => {
            apiService.logActivityEvent('STT_ERROR', { error: errorType }, currentConversationId, token);
          }}
          onOpenTalkMode={() => {
            setTalkModeOpen(true);
            // Auto-enable autoplay when Talk Mode opens
            if (!autoplayEnabled) {
              setAutoplayEnabled(true);
            }
            // If other dialogs are open, pause autoplay
            if (activeDialogsRef.current.size > 0) {
              pauseAutoplay('talk-mode');
            }
          }}
          voiceModeEligible={voiceModeEligible}
          onOpenVoiceMode={() => {
            if (voiceConsentRequired && !voiceConsentGiven) {
              setVoiceConsentOpen(true);
            } else {
              setVoiceModeActive(true);
            }
          }}
        />
      )}

      {/* Voice Mode Consent Dialog */}
      <VoiceConsentDialog
        open={voiceConsentOpen}
        onConsent={async () => {
          setVoiceConsentOpen(false);
          setVoiceConsentGiven(true);
          try {
            await apiService.recordVoiceModeConsent(token);
          } catch (err) {
            console.warn('Failed to record voice consent:', err);
          }
          setVoiceModeActive(true);
        }}
        onDecline={() => setVoiceConsentOpen(false)}
      />
      <CheckInForm open={checkInOpen} onClose={handleCloseCheckInModal} token={token} />
      <Dialog open={checkInHistoryOpen} onClose={handleCloseCheckInHistoryModal} aria-labelledby="checkin-history-dialog-title" maxWidth="sm" fullWidth > <DialogTitle id="checkin-history-dialog-title"> Check-In History <IconButton aria-label="close" onClick={handleCloseCheckInHistoryModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }} > <CloseIcon /> </IconButton> </DialogTitle> <DialogContent dividers> {checkInHistoryOpen && <CheckInHistory token={token} />} </DialogContent> <DialogActions> <Button onClick={handleCloseCheckInHistoryModal}>Close</Button> </DialogActions> </Dialog>
      <Dialog open={journalModalOpen} onClose={handleCloseJournalModal} aria-labelledby="journal-manager-dialog-title" maxWidth="lg" fullWidth > <DialogTitle id="journal-manager-dialog-title"> My Journal <IconButton aria-label="close" onClick={handleCloseJournalModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }} > <CloseIcon /> </IconButton> </DialogTitle> <DialogContent dividers> {journalModalOpen && <JournalManager token={token} />} </DialogContent> <DialogActions> <Button onClick={handleCloseJournalModal}>Close</Button> </DialogActions> </Dialog>
      <Dialog open={sessionReviewModalOpen} onClose={handleCloseSessionReviewModal} aria-labelledby="session-review-dialog-title" maxWidth="md" fullWidth > <DialogTitle id="session-review-dialog-title"> Session Review <IconButton aria-label="close" onClick={handleCloseSessionReviewModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }} > <CloseIcon /> </IconButton> </DialogTitle> <DialogContent dividers> {sessionReviewLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>} {sessionReviewError && <Alert severity="warning" sx={{ m: 1 }}>{sessionReviewError}</Alert>} {!sessionReviewLoading && !sessionReviewError && clientSummaryData && ( <> <Typography variant="h6" gutterBottom>Session Summary</Typography> <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'grey.100', borderRadius: 1 }}> <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}> {clientSummaryData.clientSummary || "No summary available."} </Typography> </Paper> <Typography variant="h6" gutterBottom>Reflection Prompts</Typography> {clientSummaryData.reflectionPrompts && clientSummaryData.reflectionPrompts.length > 0 ? ( <List dense component={Paper} elevation={0} sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1 }}> {clientSummaryData.reflectionPrompts.map((prompt, index) => ( <React.Fragment key={index}> <ListItem> <ListItemText primary={prompt} /> </ListItem> {index < clientSummaryData.reflectionPrompts.length - 1 && <Divider component="li" />} </React.Fragment> ))} </List> ) : ( <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}> No reflection prompts available for this session. </Typography> )} </> )} {!sessionReviewLoading && !sessionReviewError && !clientSummaryData && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}> No review data loaded. </Typography> )} </DialogContent> <DialogActions> <Button onClick={handleCloseSessionReviewModal}>Close</Button> </DialogActions> </Dialog>
      <Dialog open={isProgressModalOpen} onClose={handleCloseProgressModal} aria-labelledby="progress-dashboard-dialog-title" maxWidth="lg" fullWidth > <DialogTitle id="progress-dashboard-dialog-title"> My Progress Dashboard <IconButton aria-label="close" onClick={handleCloseProgressModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }} > <CloseIcon /> </IconButton> </DialogTitle> <DialogContent dividers> {isProgressModalOpen && <ProgressDashboard token={token} />} </DialogContent> <DialogActions> <Button onClick={handleCloseProgressModal}>Close</Button> </DialogActions> </Dialog>
      <Dialog open={sessionHistoryOpen} onClose={handleCloseSessionHistoryModal} aria-labelledby="session-history-dialog-title" maxWidth="md" fullWidth > <DialogTitle id="session-history-dialog-title"> Therapy Session History <IconButton aria-label="close" onClick={handleCloseSessionHistoryModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }} > <CloseIcon /> </IconButton> </DialogTitle> <DialogContent dividers> {sessionHistoryOpen && currentConversationId && ( <SessionHistory token={token} conversationId={currentConversationId} /> )} </DialogContent> <DialogActions> <Button onClick={handleCloseSessionHistoryModal}>Close</Button> </DialogActions> </Dialog>
      {/* Goal Management */}
      <GoalDialogs
        ref={goalDialogsRef}
        token={token}
        currentConversationId={currentConversationId}
        socket={socket}
        user={user}
        onSystemMessage={addSystemMessage}
        triggerAIAfterGoalAction={triggerAIAfterGoalAction}
        pauseAutoplay={pauseAutoplay}
        resumeAutoplay={resumeAutoplay}
      />

      {/* Assessment Management */}
      <AssessmentManager
        ref={assessmentManagerRef}
        token={token}
        currentConversationId={currentConversationId}
        onSystemMessage={addSystemMessage}
        triggerAIAfterGoalAction={triggerAIAfterGoalAction}
        pauseAutoplay={pauseAutoplay}
        resumeAutoplay={resumeAutoplay}
        platformSettings={platformSettings}
        userSettings={user}
        therapistSettings={therapistVoiceSettings}
        clientId={user?._id}
        autoplayEnabled={autoplayEnabled}
        chatPhase={chatPhase}
      />
      
      {/* Review Request Modal */}
      <ReviewRequestModal
        open={reviewRequestModalOpen}
        onClose={() => setReviewRequestModalOpen(false)}
        onConfirm={handleConfirmReviewRequest}
        loading={reviewRequestStatus.loading}
        rateLimitInfo={reviewRateLimitInfo}
      />

      {/* Floating Help Button — visible during onboarding (ChatControls/MobileFeatureMenu handle therapy phase) */}
      {chatPhase === 'onboarding' && !initialLoading && (
        <IconButton
          onClick={handleOpenSupportTicket}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          aria-label="Get Help"
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      )}

      {/* Support Ticket Dialog */}
      <SupportTicketDialog
        open={supportTicketOpen}
        onClose={handleCloseSupportTicket}
        token={token}
        currentPage="chat"
        chatPhase={chatPhase}
        conversationId={currentConversationId}
        currentPersona={currentPersona}
      />

      {/* Talk Mode Dialog */}
      <TalkMode
        open={talkModeOpen}
        onClose={() => {
          setTalkModeOpen(false);
          resumeAutoplay('talk-mode');
        }}
        onSendMessage={(text) => {
          // TalkMode sends via voice — mark input method before sending
          lastInputMethodRef.current = 'voice';
          handleSendMessage({ preventDefault: () => {} }, text);
        }}
        isLoading={isLoading}
        autoplayEnabled={autoplayEnabled}
        autoplayPaused={autoplayPaused}
        currentlyPlayingMessageId={currentlyPlayingMessageId}
        onAutoplayToggle={() => {
          const newState = !autoplayEnabled;
          setAutoplayEnabled(newState);
          apiService.logActivityEvent('AUTOPLAY_TOGGLED', { enabled: newState }, currentConversationId, token);
        }}
        onStopAudio={stopCurrentAudio}
        onListeningStart={handleSttListeningStart}
        onListeningEnd={handleSttListeningEnd}
        platformSettings={platformSettings}
        assignedTherapistVoiceId={assignedTherapistVoiceId}
      />
    </Paper>
  );
};

export default ChatInterface;
